import React, { useState } from "react"
import { Grid, InputAdornment, TextField } from "@material-ui/core"
import { Text, themeColors } from "letalkui"

import { ActionDialog, Notification } from "@/components"

import BoardStageCardService from "@/services/BoardStageCardService"
import ErrorHandlerService from "@/services/ErrorHandler"

import { ErrorType } from "@/hooks/useValidation"
import useMoneyInputCents from "@/hooks/useMoneyInputCents"

import useStyles from "@/components/ContactValueDialog/styles"

type ContactValueDialogProps = {
	open: boolean
	clientId: number
	currentValueInCents?: number | null
	isContactValueRequired?: boolean
	onClose: () => void
	onSaved: (contactValueInCents: number) => void
}

const ContactValueDialog: React.FC<ContactValueDialogProps> = (props) => {
	const {
		open,
		clientId,
		currentValueInCents,
		isContactValueRequired,
		onClose,
		onSaved
	} = props

	const classes = useStyles()

	const { cents, displayValue, handleKeyDown, handlePaste } = useMoneyInputCents(currentValueInCents)
	const [hasInteracted, setHasInteracted] = useState<boolean>(false)
	const [isSaving, setSaving] = useState<boolean>(false)

	const hasContactValue = cents > 0
	const valueIsInvalid = Boolean(isContactValueRequired) && !hasContactValue

	const handleClose = () => {
		setHasInteracted(false)
		onClose()
	}

	const handleSave = async (): Promise<void> => {
		if (valueIsInvalid) {
			setHasInteracted(true)
			return
		}

		setSaving(true)

		try {
			const response = await BoardStageCardService.updateContactValue({
				clientId,
				contactValueInCents: cents
			})

			onSaved(response.contactValueInCents)

			Notification.success({ message: "Valor do contato atualizado com sucesso." })

			handleClose()
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)

			Notification.error({ message: "Houve um erro ao alterar o valor do contato." })
		}

		setSaving(false)
	}

	const getValueHelperText = (): string => {
		if (!hasInteracted) {
			return ""
		}

		if (valueIsInvalid) {
			return "Informe um valor"
		}

		return ""
	}

	return (
		<ActionDialog
			title="Alterar valor do contato"
			openDialog={open}
			loading={isSaving}
			onClose={handleClose}
			onSave={handleSave}
			saveText="Salvar"
			cancelText="Cancelar"
			saveButtonDisabled={valueIsInvalid}
			fullWidth
			maxWidth="xs"
		>
			<Grid container>
				<Grid item xs={12}>
					<Text.Body
						type="small2"
						fontWeight="bold"
						color={themeColors.colors.system.gray[700]}
					>
						Valor
					</Text.Body>

					<TextField
						className={classes.valueInput}
						value={displayValue}
						variant="outlined"
						type="text"
						inputMode="decimal"
						fullWidth
						autoFocus
						InputProps={{
							readOnly: true,
							startAdornment: (
								<InputAdornment position="start">
									R$
								</InputAdornment>
							)
						}}
						onKeyDown={handleKeyDown}
						onPaste={handlePaste}
						error={hasInteracted && valueIsInvalid}
						helperText={getValueHelperText()}
					/>
				</Grid>
			</Grid>
		</ActionDialog>
	)
}

export default ContactValueDialog
